import {
    getEndpointRaw,
    postEndpointRaw,
    putEndpointRaw,
    deleteEndpointRaw,
    onResponse,
    handleErrorResp,
    patchEndpointRaw,
} from "./api"
import * as jwtUtils from "../util/jwt_utils"
import {
    formatMeta,
    formatQueryParams,
    handleResp,
    formatTagReportQueries,
    formatGoal,
    formatStripeToken,
    formatTokenUpdatePaymentSource,
} from "../util/api_utils" // eslint-disable-line no-unused-vars

// unauthHandler - a function that gets executed
// when an api call returns 401
let unauthHandler = () => {}

// setUnauthHandler - lets us pass in a function
// that gets executed when api returns 401 unauthorized
// we use this to pass in a function that dispatches sign out
// to the redux store
export const setUnauthHandler = (handler) => {
    unauthHandler = handler
}

const signInUnauthHandler = (resp) => Promise.reject(resp)

// api calls

// signIn/signUp cycle

export const signUp = onResponse(
    (resp) => {
        if (resp.status === 201) {
            return resp.json().then((json) => {
                saveAuthToken(json)
                return json
            })
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) =>
        postEndpointRaw("/v1/registration")(formatMeta(meta, "registration")),
)

export const signIn = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return resp.json().then((json) => {
                saveAuthToken(json)
                return json
            })
        }
        return handleErrorResp(resp, signInUnauthHandler)
    },
    (meta) => postEndpointRaw("/v1/users/login")(formatMeta(meta, "login")),
)

export const getProfile = onResponse((resp) => {
    if (resp.status === 200) {
        return handleResp(resp)
    }
    return handleErrorResp(resp, unauthHandler)
}, getEndpointRaw("/v1/profile"))

export const verifyEmail = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return resp.json().then((json) => {
                saveAuthToken(json)
                return json
            })
        }
        if (resp.status === 204) {
            return { status: 204 }
        } else {
            return handleErrorResp(resp, unauthHandler)
        }
    },
    postEndpointRaw((param) => `/v1/registration/confirmation${param}`),
)

// payment

export const postStripeToken = onResponse(
    (resp) => {
        if (resp.status === 201) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (tokenId, paymentPlan, teamId, cardLast4, cardBrand, cardExp) =>
        postEndpointRaw("/v1/payments/token")(
            formatStripeToken(
                tokenId,
                paymentPlan,
                teamId,
                cardLast4,
                cardBrand,
                cardExp,
            ),
        ),
)

export const getCustomer = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    getEndpointRaw((accountId) => `/v1/accounts/${accountId}/customer`),
)

export const deletePlan = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    deleteEndpointRaw((customerId) => `/v1/customers/${customerId}`),
)

export const putCustomerPaymentSourceUpdate = onResponse(
    (resp) => {
        if (resp.status === 201) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) =>
        putEndpointRaw(`/v1/customers/${meta.customerId}/payments`)(
            formatTokenUpdatePaymentSource(meta.tokenId),
        ),
)

// users

export const getUsers = onResponse((resp) => {
    if (resp.status === 200) {
        return handleResp(resp)
    }
    return handleErrorResp(resp, unauthHandler)
}, getEndpointRaw("/v1/users"))

export const putUser = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) => putEndpointRaw(`/v1/users/${meta.id}`)(formatMeta(meta, "users")),
)

export const patchUser = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) =>
        patchEndpointRaw(`/v1/users/${meta.id}`)(formatMeta(meta, "users")),
)

// password

export const forgotPassword = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return Promise.resolve(true)
        }
        return Promise.resolve(false)
    },
    postEndpointRaw(
        (email) => `/v1/password/forgot?email=${encodeURIComponent(email)}`,
    ),
)

export const resetPassword = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return Promise.resolve(true)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) => postEndpointRaw("/v1/password/reset")(formatMeta(meta)),
)

export const changePassword = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) => postEndpointRaw("/v1/password/change")(formatMeta(meta)),
)

// tags

export const getTags = onResponse((resp) => {
    if (resp.status === 200) {
        return handleResp(resp)
    }
    return handleErrorResp(resp, unauthHandler)
}, getEndpointRaw("/v1/tags"))

export const putTag = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) => putEndpointRaw(`/v1/tags/${meta.id}`)(formatMeta(meta, "tags")),
)

export const postTag = onResponse(
    (resp) => {
        if (resp.status === 201) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) => postEndpointRaw("/v1/tags")(formatMeta(meta, "tags")),
)

export const deleteTag = onResponse(
    (resp) => {
        if (resp.status === 204) {
            return Promise.resolve(true)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    deleteEndpointRaw((meta) => `/v1/tags/${meta.id}`),
)

export const getRecentTagCombos = onResponse((resp) => {
    if (resp.status === 200) {
        return handleResp(resp)
    }
    return handleErrorResp(resp, unauthHandler)
}, getEndpointRaw("/v1/reports/tags/recentlyUsed"))

export const getRecentTags = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    getEndpointRaw((meta) => buildRecentTagsQuery(meta)),
)

// tag reports

export const postTagReportQueries = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta, params) =>
        postEndpointRaw(buildTagReportsQuery(params))(
            formatTagReportQueries(meta, "tagReportRangeQueryContainers"),
        ),
)

// time records

const defaultQuery = { closedOnly: false }
const buildTimeRecordQuery = (meta, baseUrl = "/v1/timerecords") => {
    const queries = meta ? Object.assign({}, defaultQuery, meta) : defaultQuery
    const { subInterval, ...params } = queries // eslint-disable-line no-unused-vars
    return baseUrl + formatQueryParams(params)
}

const buildRecentTagsQuery = (meta) => {
    return "/v1/reports/tags" + formatQueryParams(meta)
}

const buildTagReportsQuery = (meta) => {
    return "v1/reports/tags/multirange" + formatQueryParams(meta)
}

export const getTimeRecords = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    getEndpointRaw((meta) => buildTimeRecordQuery(meta)),
)

export const postTimeRecord = onResponse(
    (resp) => {
        if (resp.status === 201) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) =>
        postEndpointRaw("/v1/timerecords")(formatMeta(meta, "timerecords")),
)

export const putTimeRecord = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) =>
        putEndpointRaw(`/v1/timerecords/${meta.id}`)(
            formatMeta(meta, "timerecords"),
        ),
)

export const deleteTimeRecord = onResponse(
    (resp) => {
        if (resp.status === 204) {
            return Promise.resolve(true)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    deleteEndpointRaw((meta) => `/v1/timerecords/${meta.id}`),
)

// comparison reports

function buildComparisonQuery(meta) {
    const { spanA, spanB, filter, ownerId, teamIds } = meta
    const filterQuery = filter === "Groups" ? "&groupByTags=true" : ""
    const ownerQuery = ownerId ? `&ownerId=${ownerId}` : ""
    const teamsQuery =
        teamIds && teamIds.length > 0 ? `&teams=${teamIds.join()}` : ""

    return (
        `/v1/reports/tags/comparison?span1StartsBy=${spanA.start}&span1EndsBy=${spanA.end}` +
        `&span2StartsBy=${spanB.start}&span2EndsBy=${spanB.end}` +
        `${filterQuery}` +
        `${ownerQuery}` +
        `${teamsQuery}`
    )
}

export const getComparisons = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    getEndpointRaw((meta) => buildComparisonQuery(meta)),
)

// helpers

function saveAuthToken(json) {
    const token = json.data.id
    jwtUtils.setToken(token)
}

// teams

export const getTeams = onResponse((resp) => {
    if (resp.status === 200) {
        return handleResp(resp)
    }
    return handleErrorResp(resp, unauthHandler)
}, getEndpointRaw("/v1/teams"))

export const postTeam = onResponse(
    (resp) => {
        if (resp.status === 201) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) => postEndpointRaw("/v1/teams")(formatMeta(meta, "teams")),
)

export const putTeam = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) => putEndpointRaw(`/v1/teams/${meta.id}`)(formatMeta(meta, "teams")),
)

// members

export const getMembers = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (id) => getEndpointRaw(`/v1/teams/${id}/members`)(),
)

export const disableMember = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    putEndpointRaw(
        ({ userId, teamId }) => `/v1/teams/${teamId}/members/${userId}/disable`,
    ),
)

export const putMember = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) =>
        putEndpointRaw(`/v1/teams/${meta.teamId}/members/${meta.id}`)(
            formatMeta(meta, "members"),
        ),
)

// invites

export const acceptInvite = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return resp.json().then((json) => {
                saveAuthToken(json)
                return json
            })
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (inviteId) => putEndpointRaw(`/v1/invites/${inviteId}/accept`)(),
)

export const getInvites = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    getEndpointRaw((meta) => "/v1/invites" + formatQueryParams(meta)),
)

export const getInvite = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (id) => getEndpointRaw(`/v1/invites/${id}`)(),
)

export const postInvite = onResponse(
    (resp) => {
        if (resp.status === 201) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) => postEndpointRaw("/v1/invites")(formatMeta(meta, "invites")),
)

export const putInvite = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) =>
        putEndpointRaw(`/v1/invites/${meta.id}`)(formatMeta(meta, "invites")),
)

export const deleteInvite = onResponse(
    (resp) => {
        if (resp.status === 204) {
            return Promise.resolve(true)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    deleteEndpointRaw((meta) => `/v1/invites/${meta.id}`),
)

export const getTimeEntriesReportCSV = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return resp
        }
        return handleErrorResp(resp, unauthHandler)
    },
    getEndpointRaw((meta) =>
        buildTimeRecordQuery(meta, "v1/export/timerecords"),
    ),
)

export const getZapierReport = onResponse(
    (resp) => {
        if (resp.stats === 200) {
            return Promise.resolve(true)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    getEndpointRaw((meta) =>
        buildTimeRecordQuery(meta, "v1/export/timerecordReport"),
    ),
)

// goals

export const getGoals = onResponse((resp) => {
    if (resp.status === 200) {
        return handleResp(resp)
    }
    return handleErrorResp(resp, unauthHandler)
}, getEndpointRaw("/v1/goals"))

export const getGoal = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    getEndpointRaw((meta) => `/v1/goals/${meta.id}`),
)

export const putGoal = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) => putEndpointRaw(`/v1/goals/${meta.id}`)(formatGoal(meta)),
)

export const postGoal = onResponse(
    (resp) => {
        if (resp.status === 201) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) => postEndpointRaw("/v1/goals")(formatGoal(meta)),
)

export const deleteGoal = onResponse(
    (resp) => {
        if (resp.status === 204) {
            return Promise.resolve(true)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    deleteEndpointRaw((meta) => `/v1/goals/${meta.id}`),
)

export const getOAuthAuthorizationUrl = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return resp.json().then((json) => Promise.resolve(json))
        }
    },
    getEndpointRaw(
        (meta) => `/oauth/authorization_url?service=${meta.service}`,
    ),
)

// accounts

export const patchAccounts = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) =>
        patchEndpointRaw(`/v1/accounts/${meta.id}`)(
            formatMeta(meta, "accounts"),
        ),
)

export const resendVerification = onResponse((resp) => {
    if (resp.status === 200) {
        return handleResp(resp)
    }
    return handleErrorResp(resp, unauthHandler)
}, postEndpointRaw(`/v1/registration/resend`))

// onboarding
export const postOnboardingProfile = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) => postEndpointRaw(`/v1/users/profile`)(formatMeta(meta, "users")),
)

//oauth

export const revokeOAuthToken = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return Promise.resolve(true)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    deleteEndpointRaw((oauthTokenID) => `/oauth/tokens/${oauthTokenID}`),
)

export const getOAuthTokens = onResponse((resp) => {
    if (resp.status === 200) {
        return handleResp(resp)
    }
    return handleErrorResp(resp, unauthHandler)
}, getEndpointRaw(`/oauth/tokens`))

// google calendar configs
export const postGoogleCalendarConfig = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta, oauthTokenId) =>
        postEndpointRaw(
            `/v1/google-calendar/config${formatQueryParams({ oauthTokenId })}`,
        )(formatMeta(meta, "googleCalendarConfig")),
)

// zoom configs
export const postZoomConfig = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta, oauthTokenId) => {
        return postEndpointRaw(
            `/v1/zoom/config${formatQueryParams({ oauthTokenId })}`,
        )(formatMeta(meta, "zoomConfig"))
    },
)

export const deleteZoomConfig = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return true
        }
        return handleErrorResp(resp, unauthHandler)
    },
    deleteEndpointRaw((id) => `v1/zoom/config?id=${id}`),
)

export const deleteGoogleCalendarConfig = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return true
        }
        return handleErrorResp(resp, unauthHandler)
    },
    deleteEndpointRaw((id) => `v1/google-calendar/config?id=${id}`),
)

export const postGoogleCalendarAccount = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta, googleCalendarConfigID) =>
        postEndpointRaw(
            `/v1/google-calendar/account${formatQueryParams({
                googleCalendarConfigID,
            })}`,
        )(formatMeta(meta, "googleCalendarAccount")),
)

export const deleteGoogleCalendarAccount = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return true
        }
        return handleErrorResp(resp, unauthHandler)
    },
    deleteEndpointRaw((id) => `v1/google-calendar/account?id=${id}`),
)

export const deleteGoogleCalendarSubscription = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return true
        }
        return handleErrorResp(resp, unauthHandler)
    },
    deleteEndpointRaw((id) => `v1/google-calendar/subscription/${id}`),
)

export const postGoogleCalendarSubscription = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) =>
        postEndpointRaw(`/v1/google-calendar/subscription`)(
            formatMeta(meta, "googleCalendarSubscription"),
        ),
)

export const putGoogleCalendarConfig = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) =>
        putEndpointRaw(`/v1/google-calendar/config/${meta.id}`)(
            formatMeta(meta, "googleCalendarConfig"),
        ),
)

export const postCalendarKeywordTag = onResponse(
    (resp) => {
        if (resp.status === 201) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) =>
        postEndpointRaw("/v1/google-calendar/tag-keyword")(
            formatMeta(meta, "googleCalendarConfigKeyword"),
        ),
)

export const putCalendarKeywordTag = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta, id) =>
        putEndpointRaw(`/v1/google-calendar/tag-keyword/${id}`)(
            formatMeta(meta, "googleCalendarConfigKeyword"),
        ),
)

export const deleteCalendarKeywordTag = onResponse(
    (resp) => {
        if (resp.status === 204) {
            return Promise.resolve(true)
        }
        if (resp.status === 404) {
            // keyword has already been deleted, do not throw error
            // this could occur because google calendar api has not been fully setup for websocket events
            // other user could have deleted event or associated tag could have been deleted, resulting in keyword deletion
            return Promise.resolve(true)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    deleteEndpointRaw((id) => `/v1/google-calendar/tag-keyword/${id}`),
)

export const postCalendarAttendeeUser = onResponse(
    (resp) => {
        if (resp.status === 201) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta) =>
        postEndpointRaw("/v1/google-calendar/attendee-user")(
            formatMeta(meta, "googleCalendarConfigAttendee"),
        ),
)

export const putCalendarAttendeeUser = onResponse(
    (resp) => {
        if (resp.status === 200) {
            return handleResp(resp)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    (meta, id) =>
        putEndpointRaw(`/v1/google-calendar/attendee-user/${id}`)(
            formatMeta(meta, "googleCalendarConfigAttendee"),
        ),
)

export const deleteCalendarAttendeeUser = onResponse(
    (resp) => {
        if (resp.status === 204) {
            return Promise.resolve(true)
        }
        return handleErrorResp(resp, unauthHandler)
    },
    deleteEndpointRaw((id) => `/v1/google-calendar/attendee-user/${id}`),
)



// WEBPACK FOOTER //
// ./src/api/client.js